import React, { useEffect, useRef, useMemo } from 'react';
import { DirectionsRenderer } from '@react-google-maps/api';
import { withDirectionsResult } from '@shared/components/DirectionsMap/withDirectionsResult';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useJoyInteractableMapContext } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { useWaitForTimeout } from '@shared/utils/hooks/useWaitForTimeout';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { MapPinWithCardV2 } from '@shared/components/JoyInteractableMap/MapPin';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelProduct, MapMarker } from '@withjoy/joykit/icons';
import { HotelMapPinWithCardProps, MapProps } from './AccommodationsTour.types';
import { JoyInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';
import { useTranslation } from '@shared/core';

const DEBOUNCE_TIMEOUT_MS = 200;
const CARD_WIDTH = 224;
const DIRECTIONS_STROKE_COLOR = '#2C2925';
const EMPTY_MARKERS: ReadonlyArray<LatLngLiteral> = [];

/**
 * Renders price information with optional strike-through price
 */
const PriceDisplay = React.memo<{ price: string; strikeOutPrice?: Maybe<string> }>(({ price, strikeOutPrice }) => (
  <Flex
    gap={2}
    paddingX={1}
  >
    {strikeOutPrice && (
      <TextV2
        typographyVariant={'body1'}
        fontSize={pxToRem(14)}
        fontWeight={500}
        textDecorationLine={'line-through'}
        color={'mono8'}
      >
        {strikeOutPrice}
      </TextV2>
    )}
    <TextV2
      typographyVariant={'body1'}
      fontSize={pxToRem(14)}
      fontWeight={600}
      color={'mono14'}
    >
      {price}
    </TextV2>
  </Flex>
));

PriceDisplay.displayName = 'PriceDisplay';

/**
 * Renders venue name with map marker icon
 */
const VenueNameDisplay = React.memo<{ name: string }>(({ name }) => (
  <Flex
    alignItems="center"
    gap={2}
  >
    <MapMarker
      size={16}
      color={'mono14'}
    />
    <TextV2
      typographyVariant={'body1'}
      fontSize={pxToRem(14)}
      fontWeight={600}
      color={'mono15'}
    >
      {name}
    </TextV2>
  </Flex>
));

VenueNameDisplay.displayName = 'VenueNameDisplay';

/**
 * Renders the appropriate label content based on available props
 */
const getPinLabel = (name?: string, price?: Maybe<string>, strikeOutPrice?: Maybe<string>) => {
  if (price) {
    return (
      <PriceDisplay
        price={price}
        strikeOutPrice={strikeOutPrice}
      />
    );
  }

  if (name) {
    return <VenueNameDisplay name={name} />;
  }

  return <HotelProduct size={20} />;
};

/**
 * A map pin component that displays hotel or venue information with an optional price card
 * @param props - Component props
 * @returns React component
 */
const HotelMapPinWithCard = React.memo((props: HotelMapPinWithCardProps) => {
  const { location, forceUpdate, name, price, strikeOutPrice, intent, showOnTop } = props;

  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <MapPinWithCardV2
      cardStyleOverride={{
        ...(isMobileOrTablet && {
          bottom: '64px',
        }),
        width: pxToRem(CARD_WIDTH),
        height: 'auto',
        padding: pxToRem(8),
        borderRadius: pxToRem(8),
        gap: pxToRem(8),
        flexDirection: 'column',
      }}
      position={{ lat: location.lat, lng: location.lng }}
      isActive={false}
      showOnTop={showOnTop}
      isCardVisible={false}
      onDoubleClick={e => {
        e.stopPropagation();
      }}
      forceUpdate={forceUpdate || isMobileOrTablet.toString()}
      label={getPinLabel(name, price, strikeOutPrice)}
      intent={intent}
    />
  );
});

// Ensure displayName is explicitly set for debugging
HotelMapPinWithCard.displayName = 'HotelMapPinWithCard';

/**
 * Root component for the tour step map display that shows hotels and venue with directions
 * @param props - Component props including directions result
 */
const TourMapRoot = (props: MapProps & { directions?: google.maps.DirectionsResult | null }) => {
  const { originList, destination, directions, property } = props;

  const { tq } = useTranslation('travelMap');
  const waitForTimeout = useWaitForTimeout();

  const mapContainerRef = useRef<HTMLDivElement>(null);

  const { map } = useJoyInteractableMapContext();
  const { getCalculatedCenterAndZoom } = useGoogleInteractableMap();

  // Memoize the markers array to prevent unnecessary recalculations
  const allMarkers = useMemo(() => (originList.length ? [...originList, destination] : EMPTY_MARKERS), [originList, destination]);

  const showAllMarkersOnMap = useEventCallback((markers: ReadonlyArray<LatLngLiteral>) => {
    waitForTimeout(DEBOUNCE_TIMEOUT_MS, () => {
      if (getCalculatedCenterAndZoom && map) {
        const centerAndZoom = getCalculatedCenterAndZoom(markers);
        map.fitBounds(centerAndZoom.bounds);
      }
    });
  });

  // Update map bounds according to the markers position to make all markers visible.
  useEffect(() => {
    if (allMarkers.length) {
      showAllMarkersOnMap(allMarkers);
    }
  }, [allMarkers, showAllMarkersOnMap]);

  return (
    <Box
      ref={mapContainerRef}
      position="relative"
      width="100%"
      height="100%"
      flex={1}
    >
      <JoyInteractableMap
        center={destination}
        autoCenterAndZoom
        radiusMiles={5}
        gestureHandling="cooperative"
      >
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: DIRECTIONS_STROKE_COLOR,
                strokeWeight: 3,
              },
            }}
          />
        )}
        <HotelMapPinWithCard
          price={property.price}
          strikeOutPrice={property.strikeOutPrice}
          location={originList[0]}
          intent={'aiBorder'}
        />
        <HotelMapPinWithCard
          name={tq`Your Venue`}
          location={destination}
          intent={'noBorder'}
          showOnTop
        />
      </JoyInteractableMap>
    </Box>
  );
};

TourMapRoot.displayName = 'TourMapRoot';

/**
 * Enhanced map component for tour steps that includes directions between hotels and venue.
 * Wrapped with withDirectionsResult HOC to handle Google Maps directions data.
 */
const HotelTourStepMap = withDirectionsResult(TourMapRoot);

HotelTourStepMap.displayName = 'HotelTourStepMap';

export { HotelTourStepMap };
